import { cn } from '@/lib/utils';

/**
 * The Den origami-fox mark, sampled directly from the brand SVG
 * (apps/ui-mobile/assets/svg/den-logo.svg). Colors are fixed brand values.
 */
export function DenMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 96 96"
      className={cn('h-8 w-8', className)}
      role="img"
      aria-label="Den"
      fill="none"
    >
      <polygon fill="#FFC400" points="37.2,22.5 55,25.4 58.1,10.2" />
      <polygon fill="#FF9400" points="46.1,34.5 55,25.4 37.2,22.5" />
      <polygon fill="#FF6200" points="55,25.4 62.2,55.2 46.1,34.5" />
      <polygon fill="#FF9400" points="55,25.4 62.2,55.2 65.6,36.5" />
      <polygon fill="#ED0001" points="46.1,34.5 49.2,50.2 25.6,55.7" />
      <polygon fill="#FF9400" points="49.2,50.2 70.4,65.8 46.1,34.5" />
      <polygon
        fill="#FF6200"
        points="54.5,65.6 49.2,50.2 61.2,59.1 70.4,65.8"
      />
      <polygon fill="#ED0001" points="70.4,65.8 64.8,78.3 42.5,85.8" />
      <polygon fill="#FF6200" points="42.5,85.8 25.6,55.7 49.2,50.2" />
      <polygon fill="#ED0001" points="49.2,50.2 42.5,85.8 54.5,65.6" />
      <polygon fill="#B80C0A" points="42.5,85.8 54.5,65.6 70.4,65.8" />
    </svg>
  );
}

/**
 * The full Den wordmark lockup (origami-fox "D" + "en"), sampled directly from
 * the brand SVG (apps/ui-mobile/assets/logo.svg). The lettering inherits the
 * current text color via `currentColor`, so it adapts to its surroundings; the
 * fox keeps its fixed brand colors.
 */
export function DenWordmark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 500 193.19"
      className={cn('h-7 w-auto', className)}
      role="img"
      aria-label="Den"
      fill="currentColor"
    >
      <path d="m168.24,140.83v27.4H13.19V13.19h155.05v52.47c3.82-4.18,8.27-7.77,13.19-10.64V0H0v181.43h181.43v-29.95c-4.92-2.87-9.38-6.46-13.19-10.64h0Z" />
      <path d="m253.55,18.71h-1.57c-8.26,0-14.95,6.69-14.95,14.95v25.69c0,2.11.66,4.17,1.86,5.91.74,1.07,1.43,2.17,2.08,3.3.13.24.3.45.5.63.13.12.26.25.38.37-1.51-1.45-3.12-2.8-4.82-4.04-7.72-5.62-17.22-8.95-27.5-8.95-25.81,0-46.74,20.93-46.74,46.74s20.93,46.74,46.74,46.74c10.28,0,19.78-3.32,27.5-8.95l1.48,4.77c.59,1.9,2.35,3.19,4.34,3.19h10.7c1.5,0,2.72-1.22,2.72-2.72V21.43c0-1.5-1.22-2.72-2.72-2.72h0Zm-44.02,112.11c-15.19,0-27.5-12.31-27.5-27.5s12.31-27.5,27.5-27.5,27.5,12.31,27.5,27.5-12.31,27.5-27.5,27.5Z" />
      <path d="m349.83,112.94c2.32,0,4.26-1.75,4.53-4.05.22-1.83.33-3.68.33-5.57,0-25.81-20.93-46.74-46.74-46.74s-46.74,20.85-46.74,46.74,20.93,46.74,46.74,46.74c17.98,0,33.59-10.16,41.41-25.04.77-1.47-.28-3.23-1.94-3.23h-15.89c-1.43,0-2.81.57-3.8,1.6-5,5.17-12.01,8.38-19.77,8.38-11.6,0-21.52-7.18-25.56-17.34-.28-.72.25-1.49,1.02-1.49h66.42,0Zm-41.88-36.17c10.83,0,20.2,6.26,24.68,15.36.36.72-.17,1.57-.98,1.57h-47.4c-.81,0-1.34-.85-.98-1.57,4.48-9.1,13.85-15.36,24.68-15.36h0Z" />
      <path d="m447.81,99.63c0-23.78-19.28-43.05-43.05-43.05-10.11,0-19.41,3.49-26.75,9.32.67-1.01,1.38-1.98,2.15-2.91.51-.61.79-1.38.79-2.17v-1.48c0-1.07-.87-1.93-1.93-1.93h-14.59c-1.5,0-2.72,1.22-2.72,2.72v39.51h0v46.72c0,1.5,1.22,2.72,2.72,2.72h13.8c1.5,0,2.72-1.22,2.72-2.72v-46.72c0-13.15,10.66-23.81,23.81-23.81s23.81,10.66,23.81,23.81h0v46.72c0,1.5,1.22,2.72,2.72,2.72h13.8c1.5,0,2.72-1.22,2.72-2.72v-46.72h0Z" />
      <g>
        <polygon
          fill="#FFC400"
          points="68.3 45.19 100.79 50.33 106.49 22.78 68.3 45.19"
        />
        <polygon
          fill="#FF9400"
          points="84.74 66.62 100.79 50.33 68.3 45.19 84.74 66.62"
        />
        <polygon
          fill="#FF6200"
          points="100.79 50.33 113.64 104.28 84.74 66.62 100.79 50.33"
        />
        <polygon
          fill="#FF9400"
          points="100.79 50.33 113.64 104.28 119.82 70.13 100.79 50.33"
        />
        <polygon
          fill="#ED0001"
          points="84.74 66.62 90.1 94.96 47.81 104.89 84.74 66.62"
        />
        <polygon
          fill="#FF9400"
          points="90.1 94.96 128.24 123.3 84.74 66.62 90.1 94.96"
        />
        <polygon
          fill="#FF6200"
          points="99.59 122.6 90.1 94.96 111.97 111.21 128.24 123.3 99.59 122.6"
        />
        <polygon
          fill="#ED0001"
          points="128.24 123.3 118.42 145.74 78.13 159.36 128.24 123.3"
        />
        <polygon
          fill="#FF6200"
          points="78.13 159.36 47.81 104.89 90.1 94.96 78.13 159.36"
        />
        <polygon
          fill="#ED0001"
          points="90.1 94.96 78.13 159.36 99.59 122.6 90.1 94.96"
        />
        <polygon
          fill="#B80C0A"
          points="78.13 159.36 99.59 122.6 128.24 123.3 78.13 159.36"
        />
      </g>
    </svg>
  );
}

/** Full Den lockup used in the header and footer. */
export function Logo({ className }: { className?: string }) {
  return <DenWordmark className={cn('h-7 w-auto text-ink-900', className)} />;
}
